import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, Modal, Popconfirm, Select, Space, message } from 'antd';
import { SaveOutlined, CloudUploadOutlined } from '@ant-design/icons';
import {
  listUnifiedTaskSavedViews,
  createUnifiedTaskSavedView,
  updateUnifiedTaskSavedView,
  deleteUnifiedTaskSavedView,
  type UnifiedTaskSavedViewRow,
} from '@/services/unifiedTask.service';

export interface UnifiedTaskSavedViewsBarProps {
  buildFiltersSnapshot: () => Record<string, unknown>;
  applyFiltersSnapshot: (filters: Record<string, unknown>) => void;
}

/**
 * Per-user saved filter sets for unified task execution boards (API: /unified/task-views).
 */
export const UnifiedTaskSavedViewsBar: React.FC<UnifiedTaskSavedViewsBarProps> = ({
  buildFiltersSnapshot,
  applyFiltersSnapshot,
}) => {
  const [savedViews, setSavedViews] = useState<UnifiedTaskSavedViewRow[]>([]);
  const [pickedId, setPickedId] = useState<string | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [overwriteSaving, setOverwriteSaving] = useState(false);

  const refresh = useCallback(() => {
    return listUnifiedTaskSavedViews()
      .then(setSavedViews)
      .catch(() => setSavedViews([]));
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleSave = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      message.warning('Enter a name');
      return;
    }
    setSaving(true);
    try {
      await createUnifiedTaskSavedView({ name: trimmed, filters: buildFiltersSnapshot() });
      message.success('Saved');
      setModalOpen(false);
      setName('');
      await refresh();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [name, buildFiltersSnapshot, refresh]);

  const handleOverwrite = useCallback(async () => {
    if (!pickedId) return;
    const v = savedViews.find((x) => x.id === pickedId);
    setOverwriteSaving(true);
    try {
      await updateUnifiedTaskSavedView(pickedId, { filters: buildFiltersSnapshot() });
      message.success(v ? `Updated “${v.name}” with current filters` : 'View updated');
      await refresh();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setOverwriteSaving(false);
    }
  }, [pickedId, savedViews, buildFiltersSnapshot, refresh]);

  const handleDelete = useCallback(async () => {
    if (!pickedId) return;
    try {
      await deleteUnifiedTaskSavedView(pickedId);
      message.success('Deleted');
      setPickedId(undefined);
      await refresh();
    } catch {
      message.error('Delete failed');
    }
  }, [pickedId, refresh]);

  return (
    <>
      <Space wrap size="small">
        <Select
          allowClear
          placeholder="Load saved view"
          style={{ minWidth: 200 }}
          value={pickedId}
          options={savedViews.map((v) => ({ value: v.id, label: v.name }))}
          onChange={(id) => {
            const sid = id ?? undefined;
            setPickedId(sid);
            if (sid) {
              const v = savedViews.find((x) => x.id === sid);
              if (v) {
                applyFiltersSnapshot(v.filters);
                message.success(`Loaded “${v.name}”`);
              }
            }
          }}
        />
        <Button icon={<SaveOutlined />} onClick={() => setModalOpen(true)}>
          Save view
        </Button>
        <Popconfirm
          title="Overwrite saved filters?"
          description="The selected view will keep its name; filter settings will match what you have on screen now."
          okText="Overwrite"
          onConfirm={() => void handleOverwrite()}
          disabled={!pickedId}
        >
          <Button
            icon={<CloudUploadOutlined />}
            disabled={!pickedId}
            loading={overwriteSaving}
          >
            Overwrite view
          </Button>
        </Popconfirm>
        <Popconfirm title="Delete the selected saved view?" onConfirm={() => void handleDelete()}>
          <Button danger disabled={!pickedId}>
            Delete
          </Button>
        </Popconfirm>
      </Space>

      <Modal
        title="Save filter view"
        open={modalOpen}
        onOk={() => void handleSave()}
        onCancel={() => {
          setModalOpen(false);
          setName('');
        }}
        confirmLoading={saving}
        destroyOnHidden
      >
        <Input
          placeholder="View name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          onPressEnter={() => void handleSave()}
        />
      </Modal>
    </>
  );
};
